/**
 * ICONOS
 * =============================================================================
 * Todos los iconos del sitio, dibujados como SVG dentro del propio código.
 *
 * -----------------------------------------------------------------------------
 * POR QUÉ SVG Y NO EMOJIS
 * -----------------------------------------------------------------------------
 * Un emoji se ve distinto en cada teléfono y en cada computadora: el mismo icono
 * es plano en un iPhone, con volumen en un Samsung y de colores raros en Windows.
 * Y en un diseño con detalles dorados como este, un emoji rompe la estética
 * por completo.
 *
 * Un SVG se ve exactamente igual en todas partes, se puede pintar del color de la
 * marca, y pesa menos que una imagen.
 *
 * -----------------------------------------------------------------------------
 * CÓMO ESTÁN HECHOS
 * -----------------------------------------------------------------------------
 * Trazo fino, sin relleno, con `stroke` del color que herede del contenedor. Van
 * dentro de un círculo dorado cuando el diseño lo pide, y la geometría de cada uno
 * sigue la de las referencias: el balón con sus pentágonos, la diana con su centro,
 * las barras de crecimiento, la silueta de dos personas.
 */

interface Props {
  /** El nombre del icono. */
  nombre: NombreIcono;
  /** Tamaño en píxeles. Por defecto 24. */
  tam?: number;
  className?: string;
}

export type NombreIcono =
  | 'balon'
  | 'barras'
  | 'diana'
  | 'personas'
  | 'trofeo'
  | 'cerebro'
  | 'corazon'
  | 'corriendo'
  | 'pantalla'
  | 'diamante'
  | 'globo'
  | 'cancha'
  | 'sobre'
  | 'pin'
  | 'calendario'
  | 'lista'
  | 'flecha'
  | 'cerrar'
  | 'menu'
  | 'descargar'
  | 'palomita'
  | 'campana'
  | 'paisaje'
  | 'instagram'
  | 'facebook'
  | 'tiktok'
  | 'desliza'
  | 'rayo';

/** El trazo común: fino, redondeado, sin relleno. */
const TRAZO = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.6,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

function Contenido({ nombre }: { nombre: NombreIcono }) {
  switch (nombre) {
    // -------------------------------------------------------------------------
    // Fútbol
    // -------------------------------------------------------------------------
    case 'balon':
      return (
        <g {...TRAZO}>
          <circle cx="12" cy="12" r="9" />
          {/* El pentágono central y las líneas hacia los vértices: es lo que hace
              que se reconozca como balón de fútbol y no como un círculo. */}
          <path d="M12 8.2 15.2 10.6 14 14.4H10L8.8 10.6z" />
          <path d="M12 8.2V3.2M15.2 10.6l4.6-1.5M14 14.4l2.9 3.9M10 14.4l-2.9 3.9M8.8 10.6 4.2 9.1" />
        </g>
      );

    case 'barras':
      return (
        <g {...TRAZO}>
          <path d="M4 20V13" />
          <path d="M9.3 20V9" />
          <path d="M14.7 20v-5" />
          <path d="M20 20V5" />
          <path d="M3 20h18" />
        </g>
      );

    case 'diana':
      return (
        <g {...TRAZO}>
          <circle cx="12" cy="12" r="9" />
          <circle cx="12" cy="12" r="4.6" />
          <circle cx="12" cy="12" r="1.2" fill="currentColor" stroke="none" />
        </g>
      );

    case 'personas':
      return (
        <g {...TRAZO}>
          <circle cx="9.2" cy="8.4" r="3.1" />
          <path d="M3.4 19.4c0-3.2 2.6-5.8 5.8-5.8s5.8 2.6 5.8 5.8" />
          <circle cx="17" cy="9.4" r="2.4" />
          <path d="M15.4 13.9c2.8.2 5 2.6 5 5.5" />
        </g>
      );

    case 'trofeo':
      return (
        <g {...TRAZO}>
          <path d="M7.4 4h9.2v5.4a4.6 4.6 0 0 1-9.2 0z" />
          <path d="M7.4 6H4.6v1.4a3.4 3.4 0 0 0 3 3.4" />
          <path d="M16.6 6h2.8v1.4a3.4 3.4 0 0 1-3 3.4" />
          <path d="M12 14v3.4" />
          <path d="M8.4 20h7.2l-.8-2.6H9.2z" />
        </g>
      );

    // -------------------------------------------------------------------------
    // Desarrollo y formación
    // -------------------------------------------------------------------------
    case 'cerebro':
      return (
        <g {...TRAZO}>
          {/* Dos lóbulos, cada uno con sus pliegues, y la línea del centro. */}
          <path d="M12 5.4v13.2" />
          <path d="M12 6.4a3 3 0 0 0-5.2-.9A2.7 2.7 0 0 0 4.4 8.6a2.9 2.9 0 0 0-.5 4.6A2.8 2.8 0 0 0 5 18a2.9 2.9 0 0 0 4.4 1.6" />
          <path d="M12 6.4a3 3 0 0 1 5.2-.9 2.7 2.7 0 0 1 2.4 3.1 2.9 2.9 0 0 1 .5 4.6A2.8 2.8 0 0 1 19 18a2.9 2.9 0 0 1-4.4 1.6" />
        </g>
      );

    case 'corazon':
      return (
        <g {...TRAZO}>
          <path d="M12 20s-7.6-4.6-7.6-9.6A4.4 4.4 0 0 1 12 7.5a4.4 4.4 0 0 1 7.6 2.9c0 5-7.6 9.6-7.6 9.6z" />
        </g>
      );

    case 'corriendo':
      return (
        <g {...TRAZO}>
          <circle cx="14.4" cy="5.4" r="1.9" />
          <path d="M13.6 9 10.4 11l1.2 3.4" />
          <path d="m11.6 14.4-2.4 4.2" />
          <path d="M13.6 9l3.2 1.6 1.8 3.4" />
          <path d="M6.4 11.6l2.8-1" />
          <path d="M9.6 11l2.4 3.4" />
        </g>
      );

    case 'pantalla':
      return (
        <g {...TRAZO}>
          <rect x="3" y="5" width="18" height="12.4" rx="2" />
          <path d="M9 21h6" />
          <path d="M12 17.4V21" />
          <path d="m11.2 9.2 3.4 2-3.4 2z" />
        </g>
      );

    case 'diamante':
      return (
        <g {...TRAZO}>
          <path d="M6 4h12l3 4.4-9 11.6L3 8.4z" />
          <path d="M3 8.4h18" />
          <path d="m9.4 4-1.6 4.4L12 20l4.2-11.6L14.6 4" />
        </g>
      );

    // -------------------------------------------------------------------------
    // Competencias
    // -------------------------------------------------------------------------
    case 'globo':
      return (
        <g {...TRAZO}>
          <circle cx="12" cy="12" r="9" />
          <path d="M3 12h18" />
          <path d="M12 3c2.6 2.6 3.9 5.5 3.9 9S14.6 18.4 12 21c-2.6-2.6-3.9-5.5-3.9-9S9.4 5.6 12 3z" />
          <path d="M4.4 7.6h15.2M4.4 16.4h15.2" />
        </g>
      );

    case 'cancha':
      return (
        <g {...TRAZO}>
          <rect x="2.6" y="5.4" width="18.8" height="13.2" rx="1.6" />
          <path d="M12 5.4v13.2" />
          <circle cx="12" cy="12" r="2.6" />
          <path d="M2.6 9.4h3.2v5.2H2.6M21.4 9.4h-3.2v5.2h3.2" />
        </g>
      );

    // -------------------------------------------------------------------------
    // Contacto
    // -------------------------------------------------------------------------
    case 'sobre':
      return (
        <g {...TRAZO}>
          <rect x="2.8" y="5.4" width="18.4" height="13.2" rx="2" />
          <path d="m3.4 6.8 8.6 6 8.6-6" />
        </g>
      );

    case 'pin':
      return (
        <g {...TRAZO}>
          <path d="M12 21s-6.6-5.6-6.6-10.2a6.6 6.6 0 0 1 13.2 0C18.6 15.4 12 21 12 21z" />
          <circle cx="12" cy="10.6" r="2.5" />
        </g>
      );

    case 'calendario':
      return (
        <g {...TRAZO}>
          <rect x="3.2" y="5.4" width="17.6" height="15.2" rx="2" />
          <path d="M3.2 10h17.6" />
          <path d="M8 3.4v4M16 3.4v4" />
          {/* Los puntos marcan días, como en la referencia. */}
          <path d="M7.6 13.6h.02M12 13.6h.02M16.4 13.6h.02M7.6 17h.02M12 17h.02" />
        </g>
      );

    case 'lista':
      return (
        <g {...TRAZO}>
          <path d="M8.4 4.6h9.2a1.8 1.8 0 0 1 1.8 1.8v12.2a1.8 1.8 0 0 1-1.8 1.8H8.4a1.8 1.8 0 0 1-1.8-1.8V6.4a1.8 1.8 0 0 1 1.8-1.8z" />
          <path d="M9.6 4.6V3.4A1 1 0 0 1 10.6 2.4h4.8a1 1 0 0 1 1 1v1.2" />
          <path d="M9.6 10.4h6.8M9.6 13.8h6.8M9.6 17.2h4" />
        </g>
      );

    // -------------------------------------------------------------------------
    // Interfaz
    // -------------------------------------------------------------------------
    case 'flecha':
      return (
        <g {...TRAZO}>
          <path d="M3.6 12h16.8" />
          <path d="m14.6 6.2 5.8 5.8-5.8 5.8" />
        </g>
      );

    case 'cerrar':
      return (
        <g {...TRAZO}>
          <path d="m5.6 5.6 12.8 12.8M18.4 5.6 5.6 18.4" />
        </g>
      );

    case 'descargar':
      // La flecha bajando hacia la bandeja: es el simbolo de instalar o guardar.
      return (
        <g {...TRAZO}>
          <path d="M12 3.4v11.2" />
          <path d="m7.4 10.2 4.6 4.6 4.6-4.6" />
          <path d="M4.4 17.2v1.6a1.8 1.8 0 0 0 1.8 1.8h11.6a1.8 1.8 0 0 0 1.8-1.8v-1.6" />
        </g>
      );

    case 'paisaje':
      // Un marco con una montana y el sol: el simbolo de «aquí van fotos».
      return (
        <g {...TRAZO}>
          <rect x="3.2" y="5" width="17.6" height="14" rx="2.4" />
          <circle cx="8.6" cy="10" r="1.8" />
          <path d="m3.6 17.4 5.2-4.8 4 3.4 3.2-2.6 4.4 4" />
        </g>
      );

    case 'campana':
      // La campana de las notificaciones.
      return (
        <g {...TRAZO}>
          <path d="M18 8.6a6 6 0 1 0-12 0c0 5.2-2.2 6.8-2.2 6.8h16.4S18 13.8 18 8.6" />
          <path d="M13.7 19a2 2 0 0 1-3.4 0" />
        </g>
      );

    case 'palomita':
      // La palomita de las listas de ventajas.
      return (
        <g {...TRAZO}>
          <path d="m4.8 12.4 4.6 4.6 9.8-10.4" />
        </g>
      );

    case 'menu':
      return (
        <g {...TRAZO} strokeWidth={1.9}>
          <path d="M3.6 6.4h16.8M3.6 12h16.8M3.6 17.6h16.8" />
        </g>
      );

    case 'desliza':
      return (
        <g {...TRAZO}>
          <path d="m7.6 9.6 4.4 4.4 4.4-4.4" />
        </g>
      );

    case 'rayo':
      return (
        <g {...TRAZO}>
          <path d="M13.4 2.6 4.8 13.4h6l-1.2 8L18.6 10h-6z" />
        </g>
      );

    // -------------------------------------------------------------------------
    // Redes sociales · con su forma reconocible
    // -------------------------------------------------------------------------
    case 'instagram':
      return (
        <g {...TRAZO}>
          <rect x="3.4" y="3.4" width="17.2" height="17.2" rx="5" />
          <circle cx="12" cy="12" r="3.9" />
          <circle cx="17" cy="7" r="1" fill="currentColor" stroke="none" />
        </g>
      );

    case 'facebook':
      return (
        <g {...TRAZO}>
          <path d="M14.8 8.4h2.4V5.2h-2.4a3.6 3.6 0 0 0-3.6 3.6v2H8.8v3.2h2.4V22h3.2v-8h2.4l.8-3.2h-3.2V9.2a.8.8 0 0 1 .8-.8z" />
        </g>
      );

    case 'tiktok':
      return (
        <g {...TRAZO}>
          <path d="M14.6 3.4v10.8a3.6 3.6 0 1 1-3.6-3.6" />
          <path d="M14.6 3.4a4.6 4.6 0 0 0 4.6 4.6" />
        </g>
      );

    default:
      return null;
  }
}

export function Icono({ nombre, tam = 24, className }: Props) {
  return (
    <svg
      width={tam}
      height={tam}
      viewBox="0 0 24 24"
      className={className}
      aria-hidden="true"
      focusable="false"
    >
      <Contenido nombre={nombre} />
    </svg>
  );
}

/**
 * El icono dentro del círculo dorado.
 *
 * Es la forma que tienen todos los iconos de las referencias: un aro dorado fino
 * con el dibujo dentro. Se hace como componente y no repitiendo el círculo en cada
 * sitio, para que el grosor del aro sea el mismo en toda la página.
 */
export function IconoEnAro({
  nombre,
  tam = 56,
  grosorIcono = 22,
}: {
  nombre: NombreIcono;
  tam?: number;
  grosorIcono?: number;
}) {
  return (
    <span className="aro" style={{ width: tam, height: tam }}>
      <Icono nombre={nombre} tam={grosorIcono} />
    </span>
  );
}
